import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { StepBlock } from 'components/StepBlock/StepBlock';
import { Button } from 'components/Button/Button';
import { Datalist } from 'components/Datalist/Datalist';
import React from 'react';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';
import { UpdateUserPayload, CreateUserPayload } from 'services/user/user.types';
import { Switch } from 'components/Switch/Switch';
import { useUserFormVM } from './useUserFormVM';
import { UpdateUserFormSchema } from '../UserFormSchema';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';

interface UserFormProps {
    mode: 'create' | 'update';
}

type FormValues<T extends 'create' | 'update'> = T extends 'create' ? CreateUserPayload : UpdateUserPayload;

export const UserForm: React.FC<UserFormProps> = ({ mode }) => {
    const { datalists } = useSelector(getDatalists);
    const { user, handleUpdateUser, handleCreateUser } = useUserFormVM();

    return (
        <>
            <Formik<FormValues<typeof mode>>
                initialValues={{
                    id: user?.id || 0,
                    firstname: user?.firstname || '',
                    lastname: user?.lastname || '',
                    departments: user?.departments || [],
                    phone: user?.phone || '',
                    email: user?.email || '',
                    role: user?.role || '',
                    online: user?.online || false,
                    color: user?.color || ''
                }}
                enableReinitialize={true}
                validationSchema={() => UpdateUserFormSchema(datalists || {})}
                onSubmit={async (values, { setSubmitting }) => {
                    if (mode === 'create') {
                        await handleCreateUser(values as CreateUserPayload);
                    } else {
                        await handleUpdateUser(values as UpdateUserPayload);
                    }
                    setSubmitting(false);
                }}
            >
                {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting, isValid }) => {
                    // Transformer values.departments (array de strings) en format MultiSelect (array d'objets)
                    const selectedDepartments =
                        values.departments?.map((deptCode: string) => ({
                            label: datalists?.departments?.[deptCode] || deptCode,
                            value: deptCode
                        })) || [];

                    // Construire les options pour le MultiSelect
                    const departmentOptions = Object.entries(datalists?.departments || {}).map(([code, label]) => ({
                        label: label,
                        value: code
                    }));

                    return (
                        <form onSubmit={handleSubmit} className="form">
                            <StepBlock title={mode === 'create' ? 'Ajouter un utilisateur' : 'Modifier un utilisateur'}>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Prénom*</label>
                                        <input
                                            type="text"
                                            name="firstname"
                                            placeholder="Maxime"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.firstname}
                                            required
                                        />
                                        {errors.firstname && touched.firstname && <p className="error">{errors.firstname}</p>}
                                    </div>

                                    <div className="form-group">
                                        <label>Nom*</label>
                                        <input
                                            type="text"
                                            name="lastname"
                                            placeholder="Navarro"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.lastname}
                                            required
                                        />
                                        {errors.lastname && touched.lastname && <p className="error">{errors.lastname}</p>}
                                    </div>

                                    <div className="form-group">
                                        <label>Numéro de téléphone*</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            placeholder="06 06 06 06 06"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.phone}
                                            required
                                        />
                                        {errors.phone && touched.phone && <p className="error">{errors.phone}</p>}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Adresse e-mail</label>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="maxime.navarro@gmail.com"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.email}
                                        />
                                        {errors.email && <p className="error">{errors.email}</p>}
                                    </div>
                                    <div className="form-group">
                                        <label>Départements*</label>
                                        <MultiSelect
                                            name="departments"
                                            isMulti={values.role === 'manager' ? true : false}
                                            placeholder="Sélectionner les départements"
                                            values={values.role === 'manager' ? selectedDepartments : selectedDepartments[0] || null}
                                            update={(e: { label: string; value: string }[] | { label: string; value: string }) => {
                                                let validDepartments: string[] = [];

                                                if (values.role === 'manager') {
                                                    // Mode multi : e est un tableau
                                                    validDepartments = Array.isArray(e)
                                                        ? e.filter((item) => item?.value !== undefined).map((item) => item.value)
                                                        : [];
                                                } else {
                                                    // Mode single : e est un objet unique
                                                    validDepartments = e && !Array.isArray(e) && e.value !== undefined ? [e.value] : [];
                                                }

                                                setFieldValue('departments', validDepartments);
                                            }}
                                            labelKey="label"
                                            valueKey="value"
                                            options={departmentOptions}
                                            colors={[]}
                                        />
                                        {errors.departments && touched.departments && <p className="error">{errors.departments}</p>}
                                    </div>
                                    <div className="form-group">
                                        <label>Rôle*</label>
                                        <Datalist
                                            list="roles"
                                            name="role"
                                            placeholder="Sélectionner un rôle"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.role}
                                            required
                                        />
                                        {errors.role && touched.role && <p className="error">{errors.role}</p>}
                                    </div>
                                </div>
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Actif ?*</label>
                                        <Switch
                                            name="online"
                                            onChange={() => setFieldValue('online', !values.online)}
                                            checked={values.online}
                                        />
                                        {errors.online && touched.online && <p className="error">{errors.online}</p>}
                                    </div>
                                </div>
                            </StepBlock>
                            <div className="actions-buttons">
                                <Button type="submit" label={mode === 'create' ? "Ajouter l'utilisateur" : "Modifier l'utilisateur"} />
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </>
    );
};
